import type { MouseEvent } from "react";

/** Last 4 characters of a session id (or the whole id if shorter). */
export function sessionIdTail(id: string): string {
  return id.length <= 4 ? id : id.slice(-4);
}

/**
 * Assign a distinct, stable accent per ISID for this browser tab session.
 * Golden-angle hues stay visually separated for the first ~dozens of sessions.
 */
const ISID_COLOR_BY_ID = new Map<string, string>();
const GOLDEN_ANGLE_DEG = 137.508;

export function colorForInteractionSessionId(id: string): string {
  const existing = ISID_COLOR_BY_ID.get(id);
  if (existing) return existing;
  const n = ISID_COLOR_BY_ID.size;
  const hue = (n * GOLDEN_ANGLE_DEG) % 360;
  const sat = 62 + (n % 3) * 8;
  const light = 58 + (n % 2) * 8;
  const color = `hsl(${hue.toFixed(1)} ${sat}% ${light}%)`;
  ISID_COLOR_BY_ID.set(id, color);
  return color;
}

export function InteractionSessionIdTailCell({
  id,
  label = "interaction_session_id",
  onClick,
  accentColor,
}: {
  id: string | null;
  label?: string;
  onClick?: (id: string, e: MouseEvent) => void;
  accentColor?: string;
}) {
  if (!id) {
    return <span className="text-text-secondary/40">·</span>;
  }
  const tail = sessionIdTail(id);
  const title = `${label}: ${id}${onClick ? " — click to filter" : ""}`;
  const pillStyle = accentColor
    ? {
        color: accentColor,
        backgroundColor: `color-mix(in srgb, ${accentColor} 18%, transparent)`,
        borderColor: `color-mix(in srgb, ${accentColor} 45%, transparent)`,
      }
    : undefined;
  const pillClass =
    "inline-block min-w-[1.35rem] rounded border px-0.5 font-mono text-[10px] tabular-nums leading-tight";
  if (onClick) {
    return (
      <button
        type="button"
        className={`${pillClass} hover:brightness-110`}
        style={pillStyle}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          onClick(id, e);
        }}
      >
        {tail}
      </button>
    );
  }
  return (
    <span className={pillClass} style={pillStyle} title={title}>
      {tail}
    </span>
  );
}
